import React, { useState, useEffect } from 'react';
import { Clock, ChevronDown, ChevronLeft, ChevronRight, Save, X, Edit, Printer, Plus, Minus } from 'lucide-react';
import { supabase } from '../../../../lib/supabase';
import '../../styles/DoubleLessonsDetails.css';

const DoubleLessonsDetails = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [sortBy, setSortBy] = useState('subject'); // 'subject' or 'class'
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [loading, setLoading] = useState(false);
  const [isModified, setIsModified] = useState(false);
  const [doubleCount, setDoubleCount] = useState(1);
  const [showTripleLessons, setShowTripleLessons] = useState(false);
  const [showQuadrupleLessons, setShowQuadrupleLessons] = useState(false);

  // Load data from Supabase
  useEffect(() => {
    loadData();
  }, []);

  // Filter and sort data when sortBy changes
  useEffect(() => {
    let sorted = [...data];
    
    if (sortBy === 'subject') {
      sorted.sort((a, b) => a.subject_code.localeCompare(b.subject_code));
    } else {
      sorted.sort((a, b) => a.class_id.localeCompare(b.class_id));
    }
    
    setFilteredData(sorted);
  }, [data, sortBy]);

  const loadData = async () => {
    try {
      setLoading(true);
      const { data: lessonsData, error } = await supabase
        .from('myclass_class_subject_item_load_quota_details_double_lessons')
        .select('*')
        .eq('status', 'active')
        .order('page_number, subject_code, class_id');

      if (error) throw error;

      setData(lessonsData || []);
      
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleItemSelect = (item) => {
    setSelectedItems(prev => {
      const exists = prev.find(selected => selected.id === item.id);
      if (exists) {
        return prev.filter(selected => selected.id !== item.id);
      } else {
        return [...prev, item];
      }
    });
    setIsModified(false);
  };

  const handleDoubleCountChange = (count) => {
    setDoubleCount(count);
    setIsModified(true);
  };

  const handleModify = () => {
    if (selectedItems.length === 0) return;
    setIsModified(true);
  };

  const handleSave = async () => {
    if (selectedItems.length === 0) return;

    try {
      const updates = selectedItems.map(item => ({
        id: item.id,
        double_count: doubleCount,
        is_modified: true,
        updated_at: new Date().toISOString()
      }));

      for (const update of updates) {
        const { error } = await supabase
          .from('myclass_class_subject_item_load_quota_details_double_lessons')
          .update({ 
            double_count: update.double_count,
            is_modified: update.is_modified,
            updated_at: update.updated_at
          })
          .eq('id', update.id);

        if (error) throw error;
      }

      // Update local data
      setData(prev => prev.map(item => {
        const updated = updates.find(update => update.id === item.id);
        return updated ? { ...item, double_count: updated.double_count, is_modified: true } : item;
      }));

      setSelectedItems([]);
      setIsModified(false);
      alert('儲存成功 (Save successful)');
      
    } catch (error) {
      console.error('Error saving:', error);
      alert('儲存失敗 (Save failed)');
    }
  };

  const handleCancel = () => {
    setSelectedItems([]);
    setDoubleCount(1);
    setIsModified(false);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleNextPage = () => {
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    } else {
      // Navigate to Triple Lessons
      setShowTripleLessons(true);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const getCurrentPageData = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredData.slice(startIndex, endIndex);
  };

  const getDisplayName = (item) => {
    return `Page ${item.page_number}--${item.subject_code}--${item.class_id}--${item.item_number}`;
  };

  const getSelectedDisplayName = (item) => {
    return `Page ${item.page_number}--${item.subject_code}--${item.class_id}--${item.item_number}`;
  };

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  // Triple Lessons Section
  if (showTripleLessons) {
    return (
      <div className="double-lessons-container">
        <div className="double-lessons-header">
          <h2>Class-Subject Item Quota Details - Triple Lessons (TBM0184E)</h2>
        </div>
        <div className="triple-lessons-content">
          <div className="triple-lessons-info">
            <h3>編修三連堂資料 (Edit Triple Lessons Data)</h3>
            <p>• 編配過程與編修連堂資料相同 (Assignment process same as double lessons)</p>
            <p>• 每筆記錄代表三個課節將連續進行 (Each record represents three consecutive lesson periods)</p>
            <div className="navigation-buttons">
              <button className="nav-btn" onClick={() => setShowQuadrupleLessons(true)}>
                Next Page
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Quadruple Lessons Section
  if (showQuadrupleLessons) {
    return (
      <div className="double-lessons-container">
        <div className="double-lessons-header">
          <h2>Class-Subject Item Quota Details - Quadruple Lessons (TBM0184E)</h2>
        </div>
        <div className="quadruple-lessons-content">
          <div className="quadruple-lessons-info">
            <h3>編修四連堂資料 (Edit Quadruple Lessons Data)</h3>
            <p>• 編配過程與編修連堂資料相同 (Assignment process same as double lessons)</p>
            <p>• 每筆記錄代表四個課節將連續進行 (Each record represents four consecutive lesson periods)</p>
            <div className="navigation-buttons">
              <button className="nav-btn" onClick={() => setShowQuadrupleLessons(false)}>
                <ChevronLeft size={16} />
                Prev Page
              </button>
              <button className="nav-btn" onClick={() => {
                setShowTripleLessons(false);
                setShowQuadrupleLessons(false);
              }}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="double-lessons-container">
      {/* Header */}
      <div className="double-lessons-header">
        <h2>Class-Subject Item Quota Details - Double Lessons (TBM0184E)</h2>
      </div>

      <div className="double-lessons-content">
        {/* Left Panel - Selected Items */}
        <div className="double-lessons-left-panel">
          <div className="selected-section">
            <label>Selected</label>
            <div className="selected-list">
              {selectedItems.map((item, index) => (
                <div key={item.id} className="selected-item">
                  {getSelectedDisplayName(item)}
                </div>
              ))}
            </div>
          </div>
          
          <div className="double-count-section">
            <label>#double</label>
            <div className="double-count-controls">
              <button 
                className="count-btn"
                onClick={() => handleDoubleCountChange(Math.max(1, doubleCount - 1))}
                disabled={doubleCount <= 1}
              >
                <Minus size={16} />
              </button>
              <input
                type="number"
                value={doubleCount}
                onChange={(e) => handleDoubleCountChange(parseInt(e.target.value) || 1)}
                min="1"
                max="5"
                className="double-count-input"
              />
              <button 
                className="count-btn"
                onClick={() => handleDoubleCountChange(Math.min(5, doubleCount + 1))}
                disabled={doubleCount >= 5}
              >
                <Plus size={16} />
              </button>
            </div>
            <div className="double-count-dropdown">
              <select 
                value={doubleCount} 
                onChange={(e) => handleDoubleCountChange(parseInt(e.target.value))}
                className="double-count-select"
              >
                <option value={1}>1</option>
                <option value={2}>2</option>
                <option value={3}>3</option>
                <option value={4}>4</option>
                <option value={5}>5</option>
              </select>
              <div className="select-arrow">
                <ChevronDown size={16} />
              </div>
            </div>
          </div>
        </div>

        {/* Middle Panel - Main Data List */}
        <div className="double-lessons-middle-panel">
          <div className="sort-container">
            <label>排序方式 (Sort by):</label>
            <div className="select-container">
              <div className="select-wrapper">
                <select 
                  value={sortBy} 
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="subject">Subject</option>
                  <option value="class">Class</option>
                </select>
                <div className="select-arrow">
                  <ChevronDown size={16} />
                </div>
              </div>
            </div>
          </div>

          <div className="data-list-container">
            <div className="list-header">PageNo-SubjItem-Class-#double</div>
            <div className="data-list">
              {getCurrentPageData().map((item, index) => (
                <div
                  key={item.id}
                  className={`data-item ${selectedItems.find(selected => selected.id === item.id) ? 'selected' : ''} ${item.is_modified ? 'modified' : ''}`}
                  onClick={() => handleItemSelect(item)}
                >
                  {getDisplayName(item)} {item.teacher_name} {item.teacher_id}
                </div>
              ))}
            </div>
            
            {/* Pagination */}
            <div className="pagination">
              <span>{currentPage}/{totalPages}</span>
            </div>
          </div>
        </div>

        {/* Right Panel - Controls */}
        <div className="double-lessons-right-panel">
          {/* Navigation Buttons */}
          <div className="nav-buttons">
            <button 
              className="nav-btn"
              onClick={handlePrevPage}
              disabled={currentPage === 1}
            >
              <ChevronLeft size={16} />
              Prev Page
            </button>
            <button 
              className="nav-btn"
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
            >
              Next Page
              <ChevronRight size={16} />
            </button>
          </div>

          {/* Action Buttons */}
          <div className="action-buttons">
            <button 
              className="action-btn modify-btn"
              onClick={handleModify}
              disabled={selectedItems.length === 0}
            >
              <Edit size={16} />
              Modify
            </button>
            <button 
              className="action-btn cancel-btn"
              onClick={handleCancel}
              disabled={!isModified}
            >
              <X size={16} />
              Cancel
            </button>
            <button 
              className="action-btn save-btn"
              onClick={handleSave}
              disabled={!isModified}
            >
              <Save size={16} />
              Save
            </button>
          </div>

          {/* Print Button */}
          <div className="print-section">
            <button 
              className="print-btn"
              onClick={handlePrint}
            >
              <Printer size={16} />
              Print...
            </button>
          </div>
        </div>
      </div>

      {/* Loading Overlay */}
      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner">載入中...</div>
        </div>
      )}
    </div>
  );
};

export default DoubleLessonsDetails;